import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import type {
  MilestoneState,
  UpdateMilestoneMutationMutation,
} from '@github-ui/server-persisted-queries/types/updateMilestoneMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {LABELS} from './constants/labels'

export type UpdateMilestoneStateVariables = {
  id: string
  number: number
  nameWithOwner: string
  state: MilestoneState
  operation: {type: 'setFields'; id: string; fields: {closed: boolean}}
}

async function updateMilestoneState({
  number,
  nameWithOwner,
  state,
}: UpdateMilestoneStateVariables): Promise<UpdateMilestoneMutationMutation> {
  let response: Response
  try {
    // The milestone is identified by `number` in the path; the companion github/github
    // MilestonesController action derives the node id server-side, so it is not sent in the body.
    response = await reactFetchJSON(`/${nameWithOwner}/milestones/${number}/state`, {
      method: 'PUT',
      body: {state},
    })
  } catch {
    throw new Error(LABELS.milestoneEditError)
  }

  // Parse the body once (best-effort). A non-2xx status or a GraphQL `errors` payload both
  // surface the generic edit error, but we thread any server-provided error messages through
  // `cause` so MilestoneEdit's `error.cause` branch can render them via formatFormErrorMessage.
  let result: GraphQLResponse<UpdateMilestoneMutationMutation> | undefined
  try {
    result = (await response.json()) as GraphQLResponse<UpdateMilestoneMutationMutation>
  } catch {
    result = undefined
  }

  const errors = result && 'errors' in result ? result.errors : undefined

  if (!response.ok || (errors && errors.length > 0)) {
    throw errors && errors.length > 0
      ? new Error(LABELS.milestoneEditError, {cause: errors})
      : new Error(LABELS.milestoneEditError)
  }

  if (!result || !('data' in result) || !result.data || !result.data.updateMilestone?.milestone) {
    throw new Error(LABELS.milestoneEditError)
  }

  return result.data
}

/**
 * TanStack Query mutation that toggles a milestone's open/closed state.
 *
 * Publishes a `setFields` operation so overlaying documents get the optimistic `closed` flip.
 */
export function useUpdateMilestoneState() {
  return useMutation<UpdateMilestoneMutationMutation, Error, UpdateMilestoneStateVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: updateMilestoneState,
    onSuccess: async data => {
      const milestone = data.updateMilestone?.milestone
      if (!milestone) throw new Error(LABELS.milestoneEditError)
      await notifySettlement(
        {type: 'setFields', id: milestone.id, fields: {closed: milestone.closed}},
        ENTITY_FIELDS_OWNER,
        DOCUMENT_FIELDS_PATH,
      )
    },
  })
}
